import type { RequestConfig } from '@umijs/max';
import { message } from 'antd';
import axios from 'axios';
import {
  tokenStorage,
  refreshTokenStorage,
  userStorage,
  emailStorage,
  roleStorage,
  refreshToken as refreshTokenApi,
} from '@/modules/infrastructure/users/services/auth.api';
import { errorConfig } from '@/requestErrorConfig';
import { applyApiBaseToRequestConfig } from '@/apiBase';

const LOGIN_PATH = '/user/login';

/**
 * Общий request вызывает errorHandler и всё равно отклоняет исходную ошибку,
 * не дожидаясь обновления токена. Поэтому повтор запроса делается здесь,
 * в перехватчике ответа: его результат получает тот, кто вызвал request.
 */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];

let refreshing: Promise<string> | null = null;
let sessionExpired = false;

function isAuthEndpoint(url?: string): boolean {
  return Boolean(url && AUTH_ENDPOINTS.some((path) => url.includes(path)));
}

function isAuthenticationError(error: any): boolean {
  if (error?.response?.status === 401) return true;
  if (error?.name === 'BizError' && Number(error?.info?.code) === 401) return true;
  const data = error?.response?.data;
  return Boolean(data && data.success === false && Number(data.code) === 401);
}

function bearerToken(headers: any): string | undefined {
  const raw =
    typeof headers?.get === 'function'
      ? headers.get('Authorization') || headers.get('authorization')
      : headers?.Authorization || headers?.authorization;
  return typeof raw === 'string' && raw.startsWith('Bearer ')
    ? raw.slice('Bearer '.length)
    : undefined;
}

/** Clear all auth data and redirect to login. Only after the refresh itself failed. */
function clearAuthAndRedirectToLogin() {
  if (sessionExpired) return;
  sessionExpired = true;
  tokenStorage.remove();
  refreshTokenStorage.remove();
  userStorage.remove();
  emailStorage.remove();
  roleStorage.remove();
  message.error('Session expired, please log in again');
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.href = LOGIN_PATH;
  }
}

/** One refresh for every request that failed with the expired access token. */
function refreshAccessToken(): Promise<string> {
  if (!refreshing) {
    const refresh = refreshTokenStorage.get();
    if (!refresh) {
      return Promise.reject(new Error('No refresh token'));
    }
    refreshing = refreshTokenApi({ refresh_token: refresh }, { skipErrorHandler: true })
      .then((res) => {
        const accessToken = res?.data?.access_token;
        if (!accessToken) {
          throw new Error(res?.message || 'Failed to refresh token');
        }
        tokenStorage.set(accessToken);
        if (res.data.refresh_token) refreshTokenStorage.set(res.data.refresh_token);
        return accessToken;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

function retryWithToken(config: any, accessToken: string) {
  const headers =
    config.headers && typeof config.headers.set === 'function'
      ? config.headers
      : { ...config.headers };
  if (typeof headers.set === 'function') {
    headers.set('Authorization', `Bearer ${accessToken}`);
  } else {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return axios.request({
    url: config.url,
    method: config.method,
    data: config.data,
    params: config.params,
    timeout: config.timeout,
    headers,
  });
}

async function recoverFromAuthError(config: any, fallbackError: unknown) {
  if (!config || isAuthEndpoint(config.url)) {
    throw fallbackError;
  }

  const latestToken = tokenStorage.get();
  const usedToken = bearerToken(config.headers);
  if (latestToken && usedToken && latestToken !== usedToken) {
    return retryWithToken(config, latestToken);
  }

  if (!refreshTokenStorage.get()) {
    clearAuthAndRedirectToLogin();
    throw fallbackError;
  }

  config._retry = true;
  let accessToken: string;
  try {
    accessToken = await refreshAccessToken();
  } catch (refreshError) {
    clearAuthAndRedirectToLogin();
    throw refreshError;
  }

  try {
    return await retryWithToken(config, accessToken);
  } catch (retryError) {
    if (isAuthenticationError(retryError)) clearAuthAndRedirectToLogin();
    throw retryError;
  }
}

async function onResponse(response: any) {
  const data = response?.data;
  const authFailed = data?.success === false && Number(data?.code) === 401;
  if (!authFailed || response?.config?._retry || isAuthEndpoint(response?.config?.url)) {
    return response;
  }
  return recoverFromAuthError(response.config, response);
}

async function onAuthError(error: any) {
  if (!isAuthenticationError(error) || isAuthEndpoint(error?.config?.url)) {
    return Promise.reject(error);
  }
  if (error?.config?._retry) {
    clearAuthAndRedirectToLogin();
    return Promise.reject(error);
  }
  try {
    return await recoverFromAuthError(error.config, error);
  } catch (refreshError) {
    return Promise.reject(refreshError);
  }
}

// Error handling function
const errorHandler = (error: any, opts?: any) => {
  if (opts?.skipErrorHandler) throw error;

  // 401 уже обработан перехватчиком: либо запрос повторён, либо сессия сброшена.
  if (isAuthenticationError(error)) throw error;

  const { response } = error;

  // Delegate other errors to the default error handler (BizError, etc.)
  const defaultHandler = errorConfig?.errorConfig?.errorHandler;
  if (defaultHandler) {
    defaultHandler(error, opts);
  } else {
    if (response && response.status) {
      const { status, data } = response;
      if (status === 403) message.error('No permission to access this resource');
      else if (status === 404) message.error('Requested resource not found');
      else if (status >= 500) message.error('Server internal error');
      else message.error(data?.detail || data?.message || `Request error ${status}`);
    } else {
      message.error('Network error, please check your network connection');
    }
  }

  throw error;
};

// Request configuration
export const requestConfig: RequestConfig = {
  // Base URL configuration (optional if you want to set a default)
  // baseURL: 'http://localhost:8000',

  timeout: 10000,

  // Request interceptor
  requestInterceptors: [
    (config: any) => {
      applyApiBaseToRequestConfig(config);

      // Add authentication header if token exists (skip for refresh endpoint — it uses refresh_token in body)
      const isRefreshRequest =
        typeof config.url === 'string' && config.url.includes('/auth/refresh');
      if (!isRefreshRequest) {
        const token = tokenStorage.get();
        if (token) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${token}`,
          };
        }
      }

      // Ensure Content-Type is set for POST requests
      if (config.method?.toLowerCase() === 'post' && !config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }

      return config;
    },
  ],

  // 401 перехватывается до errorHandler: запрос повторяется с новым токеном.
  responseInterceptors: [[onResponse, onAuthError]],

  // Error handler
  errorConfig: {
    errorHandler,
  },
};
